import { useMemo, useState, type ReactNode } from "react";
import { History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ClassificationBadge } from "@/components/ui/classification-badge";
import { useSalveazaTargetareAmbiguitate } from "@/hooks/api/mutate/useSalveazaTargetareAmbiguitate";
import { useGetTargetareScoruri } from "@/hooks/api/query/useGetTargetareScoruri";
import { useToast } from "@/hooks/use-toast";
import { ETICHETE_REGULA, REGULI_INCADRARE } from "@/utils/targetareConfig";
import { formatNumar } from "@/utils/targetareFormatting";
import {
  definitieRaportReguli,
  randuriReguli,
  rezultatRegula,
  type RandRegula,
} from "@/utils/targetareRaportReguli";
import type { ColoanaTabel } from "@/utils/targetareTabel";
import {
  FirmeCriteriuDialog,
  type FirmeCriteriuDeschis,
} from "../components/FirmeCriteriuDialog";
import { HeroPagina } from "../components/HeroPagina";
import { IstoricModificari } from "../components/IstoricModificari";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";
import { celulaAmbiguitate } from "../components/scoruri/CelulaAmbiguitate";

const REZULTATE = [
  "rezultat_micro",
  "rezultat_mica",
  "rezultat_mijlocie",
  "rezultat_mare",
];

export default function ReguliTargetare() {
  const { toast } = useToast();
  const scoruri = useGetTargetareScoruri();
  const salveaza = useSalveazaTargetareAmbiguitate();
  const [firme, setFirme] = useState<FirmeCriteriuDeschis | null>(null);
  const [istoric, setIstoric] = useState(false);
  const date = scoruri.data;
  const randuri = useMemo(() => randuriReguli(date?.ambiguitati ?? []), [date]);
  const peRegula = useMemo(() => {
    const m = new Map<string, number>();
    randuri.forEach((r) =>
      m.set(
        r.regula_text || "(fără)",
        (m.get(r.regula_text || "(fără)") ?? 0) + 1
      )
    );
    return m;
  }, [randuri]);

  const celulaBaza = useMemo(
    () =>
      celulaAmbiguitate({
        inCurs: salveaza.isPending,
        onFirme: setFirme,
        onSalveaza: (patch) =>
          salveaza.mutate(patch, {
            onSuccess: () =>
              toast({
                title: "Regula a fost salvată",
                description: "Se aplică la următoarea reîncadrare a firmelor.",
              }),
            onError: (e) =>
              toast({
                title: "Salvarea a eșuat",
                description: e.message,
                variant: "destructive",
              }),
          }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salveaza.isPending]
  );

  const celula = (
    coloana: ColoanaTabel<RandRegula>,
    r: RandRegula
  ): ReactNode => {
    if (REZULTATE.includes(coloana.id)) {
      const v = r[coloana.id as keyof RandRegula];
      return (
        <ClassificationBadge
          classification={
            typeof v === "string" ? (v as RandRegula["rezultat_mare"]) : null
          }
          size="sm"
        />
      );
    }
    return celulaBaza(
      coloana as unknown as Parameters<typeof celulaBaza>[0],
      r
    );
  };

  const istoricReguli = (date?.istoric ?? []).filter(
    (m) => m.tabel === "regula_clasificare"
  );

  return (
    <div className="space-y-6">
      <HeroPagina
        titlu="Regulile de încadrare"
        descriere={
          <>
            Fiecare serviciu NIS2 are o regulă (tipul de articol din OUG
            155/2024) care, împreună cu dimensiunea firmei, dă încadrarea:
            esențială, importantă sau în afara scopului. Aceleași reguli ca în
            formularul de notificare (fără condițiile de la art. 9, care depind
            de răspunsurile entității). Regula și marcajul „orice dimensiune” se
            editează direct; rezultatul pe fiecare dimensiune se vede în tabel,
            iar reîncadrarea aplică schimbarea.
          </>
        }
        isLoading={scoruri.isLoading}
        coloane={4}
        tiles={[
          { eticheta: "Servicii", valoare: randuri.length },
          {
            eticheta: "Esențiale la orice dimensiune",
            valoare: randuri.filter((r) =>
              ["2", "3", "4"].includes(r.regula_text)
            ).length,
            detaliu: "reguli 2, 3, 4",
          },
          {
            eticheta: "Anexa I (regula 9)",
            valoare: peRegula.get("9") ?? 0,
            detaliu: "mare esențială, mijlocie importantă",
          },
          {
            eticheta: "Anexa II (reguli 11, 12)",
            valoare: (peRegula.get("11") ?? 0) + (peRegula.get("12") ?? 0),
            detaliu: "mijlocie și mare importante",
          },
          {
            eticheta: "Servicii la orice dimensiune",
            valoare: randuri.filter((r) => r.orice_dimensiune).length,
            ton: "atentie",
          },
          {
            eticheta: "Fără regulă",
            valoare: peRegula.get("(fără)") ?? 0,
            ton: (peRegula.get("(fără)") ?? 0) > 0 ? "rau" : "bun",
            detaliu: "nu încadrează nicio firmă",
          },
          {
            eticheta: "Firme esențiale estimate",
            valoare: formatNumar(
              randuri.reduce(
                (n, r) =>
                  n +
                  (rezultatRegula(r.regula_tip, "mare") === "ESSENTIAL"
                    ? r.firme_principal_scop
                    : 0),
                0
              )
            ),
            detaliu: "prin CAEN, în scop, pe servicii esențiale la mare",
          },
        ]}
        actiuni={
          <Button variant="outline" onClick={() => setIstoric((v) => !v)}>
            <History className="mr-2 h-4 w-4" />
            Istoric ({istoricReguli.length})
          </Button>
        }
      />

      {scoruri.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Datele nu au putut fi încărcate: {scoruri.error.message}
        </p>
      )}
      {istoric && (
        <IstoricModificari
          modificari={istoricReguli}
          numeCheie={(cheie) =>
            date?.ambiguitati.find((a) => a.entity_id === cheie)?.name ?? cheie
          }
          titlu="Modificările regulilor"
        />
      )}

      <div className="rounded-md border p-3 text-sm">
        <p className="mb-1 font-medium">Legenda regulilor</p>
        <ul className="grid gap-x-6 gap-y-0.5 text-xs text-muted-foreground md:grid-cols-2">
          {REGULI_INCADRARE.map((t) => (
            <li key={t}>
              <span className="font-mono font-semibold text-foreground">
                {t}
              </span>
              : {ETICHETE_REGULA[t]}
            </li>
          ))}
        </ul>
      </div>

      <RaportInteractiv
        definitie={definitieRaportReguli}
        randuri={randuri}
        isLoading={scoruri.isLoading}
        celula={celula}
      />

      <FirmeCriteriuDialog deschis={firme} onClose={() => setFirme(null)} />
    </div>
  );
}
